
import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import staffService from '@/services/staffService';
import { checkBackendConnection, resetConnectionError } from '@/services/api';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/context/AuthContext';

// Import refactored components
import StaffTable from '@/components/staff/StaffTable';
import CreateUserDialog from '@/components/staff/CreateUserDialog';
import EditUserDialog from '@/components/staff/EditUserDialog';
import DeleteUserDialog from '@/components/staff/DeleteUserDialog';
import ResetPasswordDialog from '@/components/staff/ResetPasswordDialog';
import ConnectionErrorMessage from '@/components/staff/ConnectionErrorMessage';

// Define the User interface
interface User {
  id: string;
  username: string;
  fullName: string;
  role: string;
  email: string | null;
  active: boolean;
  permissions?: string[];
  createdAt?: string;
}

const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { user: currentUser, isAdmin } = useAuth();
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  
  // Selected user state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Fetch users from Supabase
  const { data: userData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: staffService.getStaffUsers,
    retry: 2,
    staleTime: 30000,
    meta: {
      errorHandler: (err: any) => {
        console.error('Error fetching users:', err);
      }
    }
  });

  // Effect to check data on mount
  useEffect(() => {
    console.log('User Management Page Loaded');
    console.log('Current user from context:', currentUser);
    console.log('User data from query:', userData);
  }, [currentUser, userData]);

  // Handler for retrying connection
  const handleRetryConnection = useCallback(async () => {
    setIsRetrying(true);
    resetConnectionError();
    
    try {
      const isConnected = await checkBackendConnection();
      if (isConnected) {
        refetch();
        toast('Connection Restored', {
          description: 'Successfully reconnected to the server.'
        });
      } else {
        toast('Connection Failed', {
          description: 'Unable to connect to the server. Please try again later.'
        });
      }
    } catch (error) {
      toast('Connection Error', {
        description: 'Failed to check connection status.'
      });
    } finally {
      setIsRetrying(false);
    }
  }, [refetch]);

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setIsResetPasswordDialogOpen(true);
  };
  
  // Extract and log users data
  const users = userData?.users || [];
  console.log('Users available for rendering:', users);
  console.log('User count:', users.length);
  console.log('Is admin:', isAdmin);

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading users...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add New User
        </Button>
      </div>

      <ConnectionErrorMessage 
        error={isError ? error : null} 
        isRetrying={isRetrying} 
        onRetry={handleRetryConnection} 
      />

      {!isError && (
        <div>
          {users.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-md">
              <p className="text-gray-500">No users found. Add your first user to get started.</p>
            </div>
          ) : (
            <StaffTable
              users={users}
              openEditDialog={openEditDialog}
              openDeleteDialog={openDeleteDialog}
              openResetPasswordDialog={openResetPasswordDialog}
              currentUserId={currentUser?.id}
            />
          )}
        </div>
      )}

      {/* Dialogs */}
      <CreateUserDialog 
        isOpen={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen} 
      />
      
      <EditUserDialog 
        isOpen={isEditDialogOpen} 
        onOpenChange={setIsEditDialogOpen} 
        selectedUser={selectedUser} 
        setSelectedUser={setSelectedUser} 
      />
      
      <ResetPasswordDialog 
        isOpen={isResetPasswordDialogOpen} 
        onOpenChange={setIsResetPasswordDialogOpen} 
        selectedUser={selectedUser} 
      />
      
      <DeleteUserDialog 
        isOpen={isDeleteDialogOpen} 
        onOpenChange={setIsDeleteDialogOpen} 
        selectedUser={selectedUser} 
      />
    </div>
  );
};

export default UserManagement;
